import { format, isThisYear, isToday, isYesterday } from 'date-fns';

import { Box, Card, Tag, Text, TextButton } from '@zillow/constellation-10';

import { LoadingParagraph, LoadingTag, LoadingText } from '@zg-rentals/loading-components';

import { getDisplayTextForApplicationStatus } from '../../../../utils/applicationStatusUtils';
import { getTagTypeByKey, STATUS_KEY } from '../../../../utils/statusTagUtils';

import { APPLICATION_STATUS_TYPE } from '../../../../constants/ApplicationStatusConstants';
import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';
import { EXTERNAL_ROUTES } from '../../../../constants/urls';

import SimpleCard from '../../../../components/SimpleCard';

export type Applicant = {
  name: string;
  status: string;
};

export type Application = {
  statusType: string;
  applicants: Array<Applicant>;
  applicationIsViewed: boolean;
  statusUpdatedTimestamp: number;
  conversationId: string;
  applicationId: string;
};

type Props = {
  application: Application;
  displayType?: VERTICAL_DISPLAY_TYPE;
  listingAlias: string;
  isLoading: boolean;
};

const getApplicantsDisplayText = (applicants: Array<Applicant> = []) => {
  if (!applicants.length) return '';

  let displayText = '';
  let additionalApplicantsCount = 0;

  applicants.forEach((applicant, idx) => {
    if (!idx) {
      displayText = applicant.name;
    } else {
      additionalApplicantsCount += 1;
    }
  });

  if (additionalApplicantsCount) {
    displayText += ` + ${additionalApplicantsCount}`;
  }

  return displayText;
};

const getNumTotalCompletedApplicants = (applicants: Array<{ status?: string }> = []) => {
  let count = 0;
  applicants.forEach((applicant) => {
    if (applicant.status === 'completed') count += 1;
  });
  return count;
};

const getTimestamp = (statusUpdatedTimestamp: number) => {
  const isTimestampToday = isToday(statusUpdatedTimestamp);
  const isTimestampYesterday = isYesterday(statusUpdatedTimestamp);

  let timestamp = format(statusUpdatedTimestamp, 'iiii, MMM d');

  if (!isThisYear(statusUpdatedTimestamp)) {
    timestamp = format(statusUpdatedTimestamp, 'MMM d, yyyy');
  } else if (isTimestampToday || isTimestampYesterday) {
    const formattedTime = format(statusUpdatedTimestamp, 'h:mm a');
    const dayText = isTimestampToday ? 'Today' : 'Yesterday';
    timestamp = `${dayText} at ${formattedTime}`;
  }

  return timestamp;
};

const ApplicationItem = ({
  application: { statusType, applicants, applicationIsViewed, statusUpdatedTimestamp, conversationId, applicationId },
  displayType = VERTICAL_DISPLAY_TYPE.FULL,
  listingAlias,
  isLoading,
}: Props) => {
  const applicantsDisplayName = getApplicantsDisplayText(applicants);
  const connectedStatusType = isLoading ? 'gray' : getTagTypeByKey(STATUS_KEY.INTERESTED_RENTERS, statusType);
  const isApplicationReceived = statusType === APPLICATION_STATUS_TYPE.COMPLETED;
  const statusDisplayText = getDisplayTextForApplicationStatus(statusType);
  const showNewBadge = isApplicationReceived && !applicationIsViewed;
  const numTotalCompletedApplicants = getNumTotalCompletedApplicants(applicants);

  const SubText = () => {
    if (statusType === APPLICATION_STATUS_TYPE.PENDING) {
      return (
        <div data-testid="SubTextContainer">
          <Text textStyle="body-sm" css={{ paddingTop: '200' }}>
            {`${numTotalCompletedApplicants} of ${applicants.length} have applied`}
          </Text>
        </div>
      );
    }

    const timestamp = isLoading ? null : getTimestamp(statusUpdatedTimestamp);
    return (
      <div data-testid="SubTextContainer">
        <LoadingText textStyle="body-sm" isLoading={isLoading} loadingWidth="50%">
          {timestamp}
        </LoadingText>
      </div>
    );
  };

  const handleCardClick = () => {
    let linkUrl = `${EXTERNAL_ROUTES.ZRM_HOME_PAGE}/applications/${applicationId}?propertyId=${listingAlias}`;

    if (statusType === APPLICATION_STATUS_TYPE.INVITED || statusType === APPLICATION_STATUS_TYPE.WITHDRAWN) {
      linkUrl = `${EXTERNAL_ROUTES.ZRM_HOME_PAGE}/inbox/${listingAlias}/${conversationId}`;
    }

    window.location.href = linkUrl;
  };

  const cardContent = (
    <>
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'tight' }}>
        <LoadingTag
          size="sm"
          tone={connectedStatusType}
          isLoading={isLoading}
          loadingWidth="20%"
          css={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {statusDisplayText}
        </LoadingTag>
        {showNewBadge && (
          <Tag tone="aqua" size="sm">
            NEW
          </Tag>
        )}
      </Box>
      <LoadingParagraph
        textStyle="body-sm-bold"
        isLoading={isLoading}
        loadingWidth="100%"
        css={{
          textTransform: 'capitalize',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          paddingTop: '200',
          fontSize: { lg: 'body' },
        }}
        className="fs-mask"
      >
        {applicantsDisplayName}
      </LoadingParagraph>
      <SubText />

      {displayType === VERTICAL_DISPLAY_TYPE.FULL &&
        (isLoading ? (
          <LoadingText loadingWidth="20%" textStyle="body-sm-bold" isLoading={true}>
            View
          </LoadingText>
        ) : (
          <TextButton textStyle="body-sm-bold">
            {statusType === APPLICATION_STATUS_TYPE.INVITED || statusType === APPLICATION_STATUS_TYPE.WITHDRAWN
              ? 'Message'
              : 'View'}
          </TextButton>
        ))}
    </>
  );

  if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
    return (
      <SimpleCard withChevron={true} cursor="pointer" onClick={handleCardClick} data-testid="application-item">
        {cardContent}
      </SimpleCard>
    );
  }

  return (
    <Card.Root
      tone="neutral"
      elevated={false}
      data-testid="application-item"
      css={{ height: '150px', cursor: 'pointer', padding: 'none' }}
    >
      <Card.Body onClick={handleCardClick} css={{ padding: 'Card.Root.padding' }}>
        {cardContent}
      </Card.Body>
    </Card.Root>
  );
};

export default ApplicationItem;
